import React from 'react'
import { RefreshCw } from 'lucide-react'
import { UserFile } from '../../../../../service/user'
import { ImageCard } from './ImageCard'
import { UploadArea } from './UploadArea'

interface ImageGridProps {
  files: UserFile[]
  selectedFile?: UserFile | null
  onImageClick?: (file: UserFile) => void

  // Loading states
  isLoading?: boolean
  error?: Error | null
  hasNextPage?: boolean
  isFetchingMore?: boolean
  onLoadMore?: () => void
  onRetry?: () => void

  // Upload props (for uploaded tab)
  showUploadArea?: boolean
  isUploading?: boolean
  isDragOver?: boolean
  onDragOver?: (e: React.DragEvent) => void
  onDragLeave?: (e: React.DragEvent) => void
  onDrop?: (e: React.DragEvent) => void
  onFileSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void

  // Empty state
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: () => void
  emptyActionText?: string
}

export const ImageGrid: React.FC<ImageGridProps> = ({
  files,
  selectedFile,
  onImageClick,

  isLoading = false,
  error = null,
  hasNextPage = false,
  isFetchingMore = false,
  onLoadMore,
  onRetry,

  showUploadArea = false,
  isUploading = false,
  isDragOver = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileSelect,

  emptyTitle = '이미지가 없어요!',
  emptyDescription = '이미지를 업로드하거나 생성해보세요.',
  emptyAction,
  emptyActionText,
}) => {
  const hasData = files.length > 0

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#00B7B9]"></div>
        <span className="ml-3 text-[#8F929B]">이미지를 불러오는 중...</span>
      </div>
    )
  }

  // 에러 상태
  if (error) {
    return (
      <div className="flex flex-col items-center py-12">
        <p className="mb-4 text-red-500">파일을 불러오는데 실패했습니다.</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 text-[#00B7B9] transition-colors hover:text-[#00A5A7]"
          >
            <RefreshCw size={16} />
            다시 시도
          </button>
        )}
      </div>
    )
  }

  // 데이터가 있을 때
  if (hasData) {
    return (
      <div className="flex flex-wrap justify-between gap-2 pb-6 md:justify-start">
        {/* 업로드 영역 (업로드 탭에서만 표시) */}

        <UploadArea
          isUploading={isUploading}
          isDragOver={isDragOver}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onFileSelect={onFileSelect}
        />

        {/* 이미지 리스트 */}
        {files.map((file) => (
          <ImageCard
            key={file.id}
            file={file}
            isSelected={selectedFile?.id === file.id}
            onClick={() => onImageClick?.(file)}
          />
        ))}

        {/* 더 불러오기 버튼 */}
        {hasNextPage && (
          <div className="mt-4 flex w-full justify-center">
            <button
              onClick={onLoadMore}
              disabled={isFetchingMore}
              className="flex items-center gap-2 px-4 py-2 text-[#00B7B9] transition-colors hover:text-[#00A5A7] disabled:opacity-50"
            >
              {isFetchingMore ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-[#00B7B9]"></div>
                  더 불러오는 중...
                </>
              ) : (
                '더 불러오기'
              )}
            </button>
          </div>
        )}
      </div>
    )
  }

  // 데이터가 없을 때 (빈 상태)
  return (
    <div className="flex gap-2">
      {/* 업로드 영역 (업로드 탭에서만 표시) */}

      <UploadArea
        isUploading={isUploading}
        isDragOver={isDragOver}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onFileSelect={onFileSelect}
      />
    </div>
  )
}
